/**
 * Authentication and JWT scopes type definitions
 *
 * These types support the new JWT-based scopes system
 */

// JWT Token structure with embedded scopes
export interface JWTToken {
  access_token: string
  token_type: string
  expires_in: number
  refresh_token?: string | null
  // JWT payload contains scopes
  scopes?: string[]
}

// JWT Payload structure (decoded from token)
export interface JWTPayload {
  sub: string // user_id (ShortUUID)
  email: string
  exp: number
  iat: number
  scopes: string[] // Array of permission scopes
  tenant_id?: string
}

import type { UserWithPermissionsResponse } from "@/client/types.gen"

// User interface with ShortUUID and scopes
export type User = UserWithPermissionsResponse & {
  permissions?: string[]
}

// Auth state interface
export interface AuthState {
  // State
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  scopes: string[]
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  tokenExpiry: number | null

  // Actions
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshTokenAction: () => Promise<void>
  updateUser: (user: User) => void
  updateScopes: (scopes: string[]) => void
  clearError: () => void
  setLoading: (loading: boolean) => void
  clearAllData: () => void

  // JWT Scope helpers
  hasScope: (scope: string) => boolean
  hasAnyScope: (scopes: string[]) => boolean
  hasAllScopes: (scopes: string[]) => boolean
  isAdmin: () => boolean

  // Legacy permission helpers (for backward compatibility)
  hasPermission: (permission: string) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
  hasAllPermissions: (permissions: string[]) => boolean
}

// Common scopes used in the application
export enum Scopes {
  // System administration
  SYSTEM_ADMIN = "system:admin",
  SYSTEM_READ = "system:read",
  SYSTEM_UPDATE = "system:update",

  // User management
  USERS_READ = "users:read",
  USERS_CREATE = "users:create",
  USERS_UPDATE = "users:update",
  USERS_DELETE = "users:delete",

  // File management
  FILES_READ = "files:read",
  FILES_CREATE = "files:create",
  FILES_UPDATE = "files:update",
  FILES_DELETE = "files:delete",

  // Household management
  HOUSEHOLDS_READ = "households:read",
  HOUSEHOLDS_CREATE = "households:create",
  HOUSEHOLDS_UPDATE = "households:update",
  HOUSEHOLDS_DELETE = "households:delete",

  // Session management
  SESSIONS_READ = "sessions:read",
  SESSIONS_CREATE = "sessions:create",
  SESSIONS_UPDATE = "sessions:update",
  SESSIONS_DELETE = "sessions:delete",

  // ClassTemplate management
  CLASSTEMPLATES_READ = "class_templates:read",
  CLASSTEMPLATES_CREATE = "class_templates:create",
  CLASSTEMPLATES_UPDATE = "class_templates:update",
  CLASSTEMPLATES_DELETE = "class_templates:delete",

  // Student management
  STUDENTS_READ = "students:read",
  STUDENTS_CREATE = "students:create",
  STUDENTS_UPDATE = "students:update",
  STUDENTS_DELETE = "students:delete",

  // Enrollment management
  ENROLLMENTS_READ = "enrollments:read",
  ENROLLMENTS_CREATE = "enrollments:create",
  ENROLLMENTS_UPDATE = "enrollments:update",
  ENROLLMENTS_DELETE = "enrollments:delete",

  // ClassSession management
  CLASSMEETINGS_READ = "class_sessions:read",
  CLASSMEETINGS_CREATE = "class_sessions:create",
  CLASSMEETINGS_UPDATE = "class_sessions:update",
  CLASSMEETINGS_DELETE = "class_sessions:delete",

  // Attendance management
  ATTENDANCES_READ = "attendances:read",
  ATTENDANCES_CREATE = "attendances:create",
  ATTENDANCES_UPDATE = "attendances:update",
  ATTENDANCES_DELETE = "attendances:delete",
  // [RZ-GENERATOR:SCOPES] - Do not remove this comment, used for auto-generation
}

// Login request/response types
export interface LoginRequest {
  username: string
  password: string
  grant_type?: string
}

export interface LoginResponse {
  access_token: string
  token_type: string
  expires_in: number
  refresh_token?: string | null
}

// Registration types
export interface RegisterRequest {
  email: string
  full_name: string
  password: string
}

// Password reset types
export interface PasswordResetRequest {
  email: string
}

export interface PasswordResetConfirm {
  token: string
  new_password: string
}

// User update types
export interface UserUpdateRequest {
  email?: string
  full_name?: string
}

export interface PasswordChangeRequest {
  current_password: string
  new_password: string
}

// ShortUUID validation
export const isValidShortUUID = (id: string): boolean => {
  // ShortUUID is 22 characters, alphanumeric with some special chars
  return /^[A-Za-z0-9_-]{22}$/.test(id)
}

// Legacy UUID validation (for migration compatibility)
export const isValidUUID = (id: string): boolean => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    id,
  )
}

// Utility to check if ID is ShortUUID or legacy UUID
export const getIdType = (id: string): "shortuuid" | "uuid" | "invalid" => {
  if (isValidShortUUID(id)) return "shortuuid"
  if (isValidUUID(id)) return "uuid"
  return "invalid"
}
